import React, { memo } from "react";
import { View, TouchableOpacity, Platform } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { cn } from "@/lib/utils";

export interface PageFooterProps {
  onTermsPress?: () => void;
  onPrivacyPress?: () => void;
  onHelpCenterPress?: () => void;
  testID?: string;
}

/**
 * Shared page footer — © copyright line followed by three inline links:
 * Terms of Service · Privacy Policy · Help Center.
 *
 * On web the footer renders as a full-width bar with a top border showing
 * the copyright on the left and links on the right (Figma design).
 * On native the footer is hidden (`display: none`).
 */
export const PageFooter = memo(function PageFooter({
  onTermsPress,
  onPrivacyPress,
  onHelpCenterPress,
  testID,
}: PageFooterProps) {
  const { t } = useI18n();

  const links = (
    <View className={"flex-row flex-wrap items-center gap-x-6 gap-y-1"}>
      <TouchableOpacity onPress={onTermsPress} accessibilityRole="link">
        <AppText className="text-sm text-supporting-text">
          {t("common.footer.termsOfService")}
        </AppText>
      </TouchableOpacity>
      <TouchableOpacity onPress={onPrivacyPress} accessibilityRole="link">
        <AppText className="text-sm text-supporting-text">
          {t("common.footer.privacyPolicy")}
        </AppText>
      </TouchableOpacity>
      <TouchableOpacity onPress={onHelpCenterPress} accessibilityRole="link">
        <AppText className="text-sm text-supporting-text">{t("common.footer.helpCenter")}</AppText>
      </TouchableOpacity>
    </View>
  );

  return (
    <View
      testID={testID}
      className={cn(
        "w-full border-t border-stroke-50 bg-white mt-auto",
        Platform.select({ web: "hidden tablet:flex", native: "hidden" })
      )}
    >
      <View className={`self-center w-full px-page-x py-5 flex-row items-center justify-between`}>
        <AppText className="text-sm text-supporting-text">{t("common.footer.copyright")}</AppText>
        {links}
      </View>
    </View>
  );
});
